// Broadcast coupons waiting in the header wallet pocket, as pure functions. Brief:
// docs/designkit/briefs/2026-10-16-broadcast-coupon-ribbon.md (direction changed to 02, Wallet pocket)
//
// Held in memory for the session only — deliberately not persisted. Delivery is a live SignalR
// push and the server keeps no list of a user's unclaimed broadcasts, so a reload forgets them.

/** The `CouponBroadcast` hub message, as wallet-service sends it. */
export interface ICouponBroadcastMessage {
	couponId: string;
	code: string;
	description: string;
	discountType: number;
	discountValue: number;
	message: string;
	/** Empty means everyone. */
	userIds: string[];
	expiresAt: string | null;
	sentAt: string;
}

export interface IHeldCoupon {
	couponId: string;
	code: string;
	description: string;
	discountType: number;
	discountValue: number;
	message: string;
	expiresAt: string | null;
	receivedAt: string;
	/** Set once the claim succeeds; the coupon stays listed, as claimed, until closed. */
	claimedAt?: string;
}

interface ISuppressed {
	couponId: string;
	/** Epoch ms after which a fresh broadcast of this coupon shows again. */
	until: number;
}

export interface ICouponStore {
	held: IHeldCoupon[];
	/**
	 * Coupons recently dismissed or claimed. Ignored only for `SUPPRESS_MS`: long enough that a
	 * burst or a quick resend doesn't bring back something just dealt with, short enough that a
	 * deliberate re-broadcast later (e.g. the next run of a recurring schedule) is seen.
	 */
	suppressed: ISuppressed[];
}

export const EMPTY_STORE: ICouponStore = { held: [], suppressed: [] };

export const SUPPRESS_MS = 30 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const PERCENT = 1;

const expiryMs = (expiresAt: string | null): number | null => {
	if (!expiresAt) return null;
	const ms = new Date(expiresAt).getTime();
	return Number.isNaN(ms) ? null : ms;
};

export const isExpired = (expiresAt: string | null, now: number) => {
	const ms = expiryMs(expiresAt);
	return ms !== null && ms <= now;
};

export const expiresWithinADay = (expiresAt: string | null, now: number) => {
	const ms = expiryMs(expiresAt);
	return ms !== null && ms > now && ms - now <= DAY_MS;
};

/**
 * Whether this broadcast is for this user. Decided per message — the popup this replaces set a
 * flag once and never cleared it, so one targeted broadcast made every later one show too.
 */
export const isTargeted = (message: Pick<ICouponBroadcastMessage, 'userIds'>, userId: string) =>
	!message.userIds?.length || message.userIds.includes(userId);

const isSuppressed = (store: ICouponStore, couponId: string, now: number) =>
	store.suppressed.some((s) => s.couponId === couponId && s.until > now);

/** Restarts the window for this coupon, dropping any lapsed entries on the way. */
const suppress = (store: ICouponStore, couponId: string, now: number): ISuppressed[] => [
	...store.suppressed.filter((s) => s.couponId !== couponId && s.until > now),
	{ couponId, until: now + SUPPRESS_MS },
];

/** Adds a live arrival. `added` is false when nothing new appeared, so the caller knows not to chime. */
export const receiveCoupon = (
	store: ICouponStore,
	message: ICouponBroadcastMessage,
	now: number
): { store: ICouponStore; added: boolean } => {
	if (!message.couponId || isExpired(message.expiresAt, now) || isSuppressed(store, message.couponId, now)) {
		return { store, added: false };
	}
	// Past the window: forget lapsed suppressions so the list doesn't grow all session.
	const live: ICouponStore = { ...store, suppressed: store.suppressed.filter((s) => s.until > now) };

	const coupon: IHeldCoupon = {
		couponId: message.couponId,
		code: message.code,
		description: message.description,
		discountType: message.discountType,
		discountValue: message.discountValue,
		message: message.message,
		expiresAt: message.expiresAt,
		receivedAt: new Date(now).toISOString(),
	};

	const existing = live.held.find((h) => h.couponId === message.couponId);
	if (existing && !existing.claimedAt) {
		// Re-sent while still waiting unclaimed: refresh its text, but it isn't a new arrival.
		const held = live.held.map((h) => (h === existing ? { ...coupon, receivedAt: h.receivedAt } : h));
		return { store: { ...live, held }, added: false };
	}
	if (existing) {
		// Re-broadcast after the window, while still listed as claimed: a fresh offer replaces it.
		return { store: { ...live, held: live.held.map((h) => (h === existing ? coupon : h)) }, added: true };
	}
	return { store: { ...live, held: [...live.held, coupon] }, added: true };
};

export const dismissCoupon = (store: ICouponStore, couponId: string, now: number): ICouponStore => {
	if (!store.held.some((h) => h.couponId === couponId)) return store;
	return {
		held: store.held.filter((h) => h.couponId !== couponId),
		suppressed: suppress(store, couponId, now),
	};
};

export const undoDismiss = (store: ICouponStore, coupon: IHeldCoupon): ICouponStore => ({
	held: store.held.some((h) => h.couponId === coupon.couponId) ? store.held : [...store.held, coupon],
	suppressed: store.suppressed.filter((s) => s.couponId !== coupon.couponId),
});

export const markClaimed = (store: ICouponStore, couponId: string, now: number): ICouponStore => {
	if (!store.held.some((h) => h.couponId === couponId)) return store;
	return {
		held: store.held.map((h) => (h.couponId === couponId ? { ...h, claimedAt: new Date(now).toISOString() } : h)),
		suppressed: suppress(store, couponId, now),
	};
};

/** Clears out a claimed coupon the user has finished looking at. Already suppressed, so no undo. */
export const removeHeld = (store: ICouponStore, couponId: string): ICouponStore => ({
	...store,
	held: store.held.filter((h) => h.couponId !== couponId),
});

/** What the pocket lists: claimed ones plus unexpired ones, soonest expiry first, open-ended last. */
export const visibleCoupons = (store: ICouponStore, now: number): IHeldCoupon[] =>
	store.held
		.filter((h) => h.claimedAt || !isExpired(h.expiresAt, now))
		.sort((a, b) => (expiryMs(a.expiresAt) ?? Infinity) - (expiryMs(b.expiresAt) ?? Infinity));

export const formatCouponValue = (coupon: Pick<IHeldCoupon, 'discountType' | 'discountValue'>) =>
	coupon.discountType === PERCENT
		? `${coupon.discountValue.toLocaleString('en-NG', { maximumFractionDigits: 2 })}%`
		: `₦${coupon.discountValue.toLocaleString('en-NG', { maximumFractionDigits: 2 })}`;

export const expiryLabel = (expiresAt: string | null, now: number): string => {
	const ms = expiryMs(expiresAt);
	if (ms === null) return 'No expiry';
	const date = new Date(ms);
	const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
	if (new Date(now).toDateString() === date.toDateString()) return `Expires today, ${time}`;
	if (expiresWithinADay(expiresAt, now)) return `Expires tomorrow, ${time}`;
	return `Expires ${date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
};

/** Pulls a readable reason out of an RTK Query error, whatever envelope the wallet service used. */
export const claimErrorMessage = (error: unknown): string => {
	const data = (error as { data?: unknown } | undefined)?.data;
	if (typeof data === 'string' && data.trim()) return data;
	if (data && typeof data === 'object') {
		const d = data as Record<string, unknown>;
		for (const key of ['message', 'detail', 'error', 'title']) {
			if (typeof d[key] === 'string' && d[key].trim()) return d[key];
		}
	}
	return "Couldn't claim this coupon. Please try again.";
};
